import React, { FC, useCallback, useState } from "react";
import { Block } from "kanopi-components/Block";
import { Button, SIZE, TButtonProps } from "kanopi-components/Button";
import { Modal } from "kanopi-components/Modal";
import { ClaimStatusChoices } from "kw/api/zeus";
import { useStyletron } from "kanopi-components/theme/useStyletron";
import { Select } from "kanopi-components/Select";
import { ParagraphSmall } from "kanopi-components/typography";

type TAdminCloseClaimModal = {
  openModal: (arg: boolean) => void;
  claim: TClaim;
  closeClaim: (decision: ClaimStatusChoices) => void;
};

export const AdminCloseClaimModal: FC<TAdminCloseClaimModal> = ({
  openModal,
  claim,
  closeClaim,
}) => {
  const [, theme] = useStyletron();
  const [decision, setDecision] = React.useState("");

  const handleSubmit = useCallback(() => {
    closeClaim(decision);
    openModal(false);
  }, [decision]);

  const onClose = useCallback(() => {
    openModal(false);
  }, []);

  const decisionOptions = [
    { label: "Paid", value: ClaimStatusChoices.PAID },
    { label: "Rejected", value: ClaimStatusChoices.REJECTED },
    { label: "Other", value: ClaimStatusChoices.OTHER },
  ];

  return (
    <Modal
      onClose={onClose}
      closeable
      isOpen={true}
      animate
      autoFocus
      overrides={{
        Dialog: {
          style: () => ({
            width: "600px",
          }),
        },
      }}
    >
      <Modal.Header $style={{ textAlign: "center" }}>
        Claim closure confirmation
      </Modal.Header>
      <Modal.Body $style={{ marginTop: theme.sizing.scale800 }}>
        <Block width="100%">
          <ParagraphSmall>
            You are about to close claim number <b>{claim?.id}</b>. Please note that you
            will not be able to reopen this claim once confirmed.
          </ParagraphSmall>
          <ParagraphSmall>
            A confirmation email to the customer{" "}
            <b style={{ wordWrap: "break-word" }}>
              {claim?.claimProfile?.owner?.email}
            </b>{" "}
            will be sent upon confirmation.
          </ParagraphSmall>
          <Select
            clearable={false}
            searchable={false}
            options={decisionOptions}
            labelKey="label"
            valueKey="value"
            placeholder="Reason for closure"
            onChange={(value) => setDecision(value)}
            value={decision}
          />
        </Block>
      </Modal.Body>
      <Modal.Footer>
        <Button
          data-testid="closeClaimConfirmButton"
          onClick={handleSubmit}
          disabled={!decision}
        >
          Confirm and close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

type TAdminCloseClaimButton = {
  claim: TClaim;
  closeClaim: (decision: ClaimStatusChoices) => void;
} & TButtonProps;

export const AdminCloseClaimButton: FC<TAdminCloseClaimButton> = ({
  claim,
  closeClaim,
  ...props
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const openModal = (open: boolean) => {
    setIsOpen(open);
  };

  const onClick = useCallback(() => {
    openModal(true);
  }, []);

  const { size, ...restProps } = props;

  return (
    <>
      <Button
        size={size || SIZE.compact}
        outlined={true}
        onClick={onClick}
        {...restProps}
      >
        Close claim
      </Button>

      {isOpen && (
        <AdminCloseClaimModal
          openModal={openModal}
          claim={claim}
          closeClaim={closeClaim}
        />
      )}
    </>
  );
};
